import React from 'react';
import { Icon } from './components.jsx';
// Temporary Access Pass - issue a Microsoft Entra TAP to a locked-out user.
// Triage is the front-end + audit; the actual issuance runs on Ed's isolated
// tap-issuance-lt function (managed identity), called by POST /api/tap. This
// screen is IT-only AND limited to the TAP_ISSUERS allowlist server-side.
// The pass is shown once here and never stored.

const { useState, useEffect, useCallback, useRef, useMemo } = React;

const tapApi = (method, path, body) => window.IT_AUTH.api(method, path, body, 'tap');

// Members of this group are eligible to RECEIVE a pass (matches Ed's server-side
// guard). Picker is sourced from it so you only ever see users you can issue to.
const ELIGIBLE_GROUP_ID = 'f222f793-ba83-44a7-b183-4b7c4d9046e9';

// Session cache. Paginated, so no 100-member cap (unlike the old Power Automate
// picker). Names sorted for the dropdown.
let _eligibleCache = null;
export async function fetchEligibleUsers() {
  if (_eligibleCache) return _eligibleCache;
  const token = await window.IT_AUTH.getToken();
  let all = [];
  let url = `https://graph.microsoft.com/v1.0/groups/${ELIGIBLE_GROUP_ID}/members?$select=id,displayName,mail,userPrincipalName,jobTitle&$top=999`;
  while (url) {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Graph /groups/members → ${res.status}`);
    const data = await res.json();
    all = all.concat(data.value || []);
    url = data['@odata.nextLink'] || null;
  }
  all.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  _eligibleCache = all;
  return all;
}

export const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  // The issuance function returns the expiry as a plain "HH:mm" string, not an
  // ISO timestamp - show it as-is rather than blanking it. ISO values (audit
  // createdAt) still format normally.
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};
export const upnOf = (u) => (u.userPrincipalName || u.mail || '').toLowerCase();

const STATUS_STYLE = {
  issued:  { color: '#065F46', bg: '#ECFDF5', label: 'Issued' },
  refused: { color: '#92400E', bg: '#FFFBEB', label: 'Refused' },
  error:   { color: '#991B1B', bg: '#FEF2F2', label: 'Error' },
};

// Official flat Microsoft Azure logo (mark + wordmark), inlined as SVG because
// the app CSP blocks external images. Single path, brand blue #0089d6. Aspect
// ratio ~3.47:1 - set height, width scales from the viewBox.
function AzureLogo({ height = 22 }) {
  return (
    <svg viewBox="0 0 52.91666 15.244921" height={height} width={height * (52.91666 / 15.244921)}
      xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Microsoft Azure"
      style={{ display: 'block', flex: '0 0 auto' }}>
      <g transform="translate(677.9313,-313.85407)">
        <g transform="matrix(0.03978217,0,0,0.03978217,-658.51488,317.36166)">
          <path fill="#0089d6" d="m -259.18627,274.57516 c 63.5635,-11.22863 116.06332,-20.52032 116.66629,-20.64819 l 1.09632,-0.2325 -60.01021,-71.38026 c -33.00559,-39.25915 -60.01018,-71.53249 -60.01018,-71.71853 0,-0.35223 61.96561,-170.992127 62.31396,-171.599207 0.11647,-0.20297 42.28595,72.60148 102.221586,176.482797 56.11284,97.25555 102.3751556,177.4431 102.8051496,178.19452 l 0.78184,1.36624 -190.7173956,-0.0246 -190.71736,-0.0245 115.57,-20.4157 z m 731.37889,-17.26291 c -29.03093,-1.86128 -45.91544,-18.39214 -50.38974,-49.33422 -1.19021,-8.2311 -1.19476,-8.44466 -1.31656,-61.88078 l -0.11789,-51.717227 h 12.84834 12.8483 l 0.10118,50.023887 c 0.0912,45.08514 0.14842,50.37131 0.57946,53.54292 1.74935,12.87168 5.23201,21.5276 11.16124,27.74067 4.74528,4.97242 10.30038,7.8839 17.99011,9.42882 3.62783,0.72884 13.94813,0.7303 17.25569,0.003 7.79588,-1.7156 14.0442,-5.10811 19.55617,-10.61799 6.28249,-6.28012 10.93015,-15.1903 13.17899,-25.26595 l 0.75793,-3.39586 0.0844,-50.44723 0.0844,-50.447217 h 13.11732 13.11735 v 79.304447 79.30444 h -12.98222 -12.98222 v -12.5824 c 0,-8.54933 -0.0937,-12.55118 -0.29222,-12.485 -0.16069,0.0536 -0.82744,1.07357 -1.48166,2.26667 -4.47373,8.15896 -11.92745,15.62029 -20.09501,20.1155 -9.79024,5.38827 -19.60858,7.30422 -33.02333,6.44412 z m 294.66636,-0.12816 c -10.24795,-0.7703 -21.03846,-4.29359 -29.85796,-9.74908 -18.58873,-11.49853 -29.58761,-30.45184 -32.80805,-56.53499 -1.11441,-9.02575 -1.24835,-21.14964 -0.3184,-28.81907 2.07786,-17.13682 8.81185,-33.95976 18.40106,-45.96982 2.45824,-3.07883 8.03495,-8.65553 11.11365,-11.11361 8.31379,-6.637877 18.03843,-11.279617 28.36334,-13.538327 6.01579,-1.31603 16.60968,-1.93394 23.00111,-1.34159 16.05576,1.48805 30.77105,9.04765 40.77267,20.945887 10.1615,12.08847 15.74681,28.98006 16.46007,49.77987 0.11179,3.25966 0.13889,8.97466 0.0603,12.7 l -0.143,6.77333 -56.23278,0.0712 -56.23278,0.0712 v 2.5094 c 0,7.63915 1.85722,16.33991 5.06795,23.74265 2.76877,6.38373 7.53485,13.35462 11.43714,16.72802 8.00131,6.91687 17.79386,11.05701 28.50936,12.05334 3.97318,0.3694 14.09889,-0.0195 18.62666,-0.7152 12.91802,-1.98524 25.19946,-7.052 35.11745,-14.48783 1.16688,-0.87486 2.28357,-1.69223 2.48152,-1.81635 0.28968,-0.18164 0.35814,2.10408 0.35081,11.71222 l -0.009,11.93792 -2.65743,1.64355 c -11.21971,6.93911 -24.07305,11.39481 -37.68211,13.06277 -4.06135,0.49775 -18.96443,0.71949 -23.82142,0.35441 z m 48.93919,-100.68528 c 0,-12.79609 -5.39245,-27.01096 -13.02337,-34.3305 -5.44587,-5.22367 -12.02921,-8.41603 -19.85552,-9.62822 -3.70264,-0.5735 -11.50631,-0.35262 -15.41004,0.43617 -8.25234,1.66748 -15.07811,5.29536 -21.03154,11.17821 -6.26561,6.19133 -10.96323,13.71122 -13.91756,22.27909 -1.06234,3.08091 -2.30488,8.13901 -2.69056,10.95257 l -0.18376,1.34056 h 43.05616 43.05619 z M 53.010852,253.20058 c 0.06587,-0.19403 19.161194,-50.3586 42.434086,-111.47682 l 42.314342,-111.124037 13.59583,-8.5e-4 13.5958,-8.4e-4 1.12805,2.89278 c 3.4472,8.84008 84.71032,219.821587 84.71032,219.931197 0,0.0722 -6.50875,0.13039 -14.46389,0.12923 l -14.46389,-0.003 -11.71222,-31.18282 -11.71222,-31.18281 -47.15885,-5.7e-4 -47.15886,-5.6e-4 -0.40982,1.05833 c -0.22538,0.58208 -5.229376,14.61335 -11.120031,31.18062 l -10.710221,30.12225 -14.494087,0.005 c -11.4727,0.004 -14.469109,-0.0686 -14.374339,-0.34784 z M 189.82928,167.38571 c 0,-0.0494 -7.88187,-21.41719 -17.51527,-47.48389 -18.02243,-48.766297 -19.03061,-51.700447 -20.45454,-59.529577 -0.66771,-3.67122 -1.00556,-3.77324 -1.40188,-0.42333 -0.2833,2.39448 -1.51167,7.75245 -2.45866,10.72445 -0.46988,1.47461 -8.58577,23.74972 -18.03533,49.500237 -9.44954,25.75052 -17.18099,46.92777 -17.18099,47.06055 0,0.13279 17.33551,0.24143 38.52334,0.24143 21.18783,0 38.52333,-0.0404 38.52333,-0.0899 z m 69.70889,82.10639 v -4.06127 l 46.98461,-64.58929 46.98461,-64.5893 -42.53961,-0.14111 -42.53961,-0.14111 -0.0739,-10.795 -0.0739,-10.794997 h 61.52781 61.5278 v 3.64606 3.646057 l -46.99,64.94153 c -25.8445,35.71785 -46.99,65.00162 -46.99,65.07506 0,0.0734 20.8915,0.13352 46.42555,0.13352 h 46.42556 v 10.86555 10.86556 h -65.33447 -65.33445 z m 344.78149,3.8731 c -0.10349,-0.10346 -0.18816,-35.91746 -0.18816,-79.58666 V 94.380023 h 12.84112 12.84111 v 16.368887 c 0,9.00289 0.10947,16.36889 0.2433,16.36889 0.13383,0 0.75881,-1.47368 1.38884,-3.27484 2.88711,-8.2536 7.89393,-15.96572 14.34443,-22.09509 5.81095,-5.521657 12.45235,-8.823637 20.28898,-10.087287 2.20134,-0.35497 4.064,-0.42274 8.46667,-0.30806 5.52924,0.14402 8.42001,0.54819 11.78278,1.64738 l 1.05833,0.34594 v 13.328937 13.32893 l -3.03389,-1.51868 c -5.3592,-2.68267 -10.64714,-3.74429 -17.00389,-3.41375 -4.13707,0.21513 -6.85252,0.74636 -10.16,1.98766 -6.80519,2.55399 -12.32518,7.06198 -16.18228,13.2155 -5.58374,8.90817 -9.56612,20.35873 -10.7407,30.88287 -0.21079,1.88864 -0.33508,17.27019 -0.38459,47.59327 l -0.0731,44.80278 H 617.1582 c -6.95771,0 -12.73506,-0.0847 -12.83852,-0.18816 z M -488.0685,252.80762 c 0,-0.1005 28.27652,-49.18632 62.83671,-109.07964 l 62.8367,-108.896937 73.22891,-61.45385 c 40.2759,-33.79961 73.33737,-61.49516 73.46992,-61.54567 0.13255,-0.0505 -0.39727,1.28299 -1.17738,2.96333 -0.78011,1.68034 -36.56244,78.4296699 -79.51629,170.55406 l -78.09789,167.498887 -56.79034,0.0712 c -31.23468,0.0392 -56.79034,-0.011 -56.79034,-0.11142 z" />
        </g>
      </g>
    </svg>
  );
}

export function TapView({ mobile = false } = {}) {
  const [members, setMembers] = useState(null); // null = loading
  const [membersErr, setMembersErr] = useState(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [issuing, setIssuing] = useState(false);
  const [result, setResult] = useState(null); // { status, pass, tapExpiresAt, detail }
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState(null);
  const pickerRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try { setHistory(await tapApi('GET', '/tap') || []); }
    catch (_) { setHistory([]); }
  }, []);

  useEffect(() => {
    fetchEligibleUsers().then(setMembers).catch((e) => { setMembersErr(e.message); setMembers([]); });
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    const h = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const matches = useMemo(() => {
    if (!members) return [];
    const q = query.trim().toLowerCase();
    // No cap: the dropdown scrolls (maxHeight + overflow), and members is just
    // the eligible group, so showing everyone is fine. Empty query lists all.
    if (!q) return members;
    return members.filter((m) =>
      (m.displayName || '').toLowerCase().includes(q) || upnOf(m).includes(q));
  }, [members, query]);

  const issue = async () => {
    if (!selected || issuing) return;
    setIssuing(true); setResult(null); setCopied(false);
    try {
      const r = await tapApi('POST', '/tap', { targetUpn: upnOf(selected) });
      setResult(r);
    } catch (e) {
      setResult({ status: 'error', detail: e.message || 'Request failed' });
    } finally {
      setIssuing(false);
      loadHistory();
    }
  };

  const copyPass = () => {
    if (!result?.pass) return;
    navigator.clipboard?.writeText(result.pass).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const reset = () => { setSelected(null); setQuery(''); setResult(null); setCopied(false); };

  return (
    <div style={{ height: '100%', overflowY: 'auto', background: 'var(--bg)' }}>
      <div style={{ maxWidth: mobile ? '100%' : 760, margin: '0 auto', padding: mobile ? '16px 14px 32px' : '24px 28px', boxSizing: 'border-box' }}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: mobile ? 'center' : 'flex-start' }}><AzureLogo height={22}/></div>
        <h1 style={{ margin: 0, fontSize: mobile ? 17 : 20, fontWeight: 700, color: 'var(--text)', textAlign: mobile ? 'center' : 'left' }}>Microsoft Azure Temporary Access Pass</h1>
        <p style={{ margin: '4px 0 20px', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, textAlign: mobile ? 'center' : 'left' }}>
          Issue a one-time Entra sign-in pass to a locked-out user. Single use, expires in 1 hour. Only eligible, non-admin users can be issued a pass.
        </p>

        {/* Issue card */}
        <div style={{ border: '1px solid var(--line)', borderRadius: 8, background: 'var(--bg-elev)', padding: '16px 18px' }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>User to issue a pass to</label>
          <div ref={pickerRef} style={{ position: 'relative', marginTop: 6 }}>
            {selected ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '9px 12px', border: '1px solid var(--line)', borderRadius: 6, background: 'var(--bg)' }}>
                <span style={{ fontSize: 14, color: 'var(--text)' }}>
                  {selected.displayName} <span style={{ color: 'var(--text-subtle)', fontFamily: 'var(--mono)', fontSize: 12 }}>{upnOf(selected)}</span>
                </span>
                <button className="btn" onClick={reset} title="Clear"><Icon name="close" size={12}/></button>
              </div>
            ) : (
              <input
                type="text" value={query}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                placeholder={members === null ? 'Loading users…' : 'Search by name or email'}
                disabled={members === null && !membersErr}
                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid var(--line)', borderRadius: 6, fontSize: mobile ? 16 : 14, background: 'var(--bg-elev)', color: 'var(--text)', outline: 'none' }}
              />
            )}
            {open && !selected && matches.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 6, boxShadow: 'var(--shadow-pop)', maxHeight: 280, overflowY: 'auto', zIndex: 20 }}>
                {matches.map((m) => (
                  <div key={m.id} onClick={() => { setSelected(m); setOpen(false); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--line)' }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{m.displayName}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{upnOf(m)}{m.jobTitle ? ` · ${m.jobTitle}` : ''}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {membersErr && <div style={{ fontSize: 12, color: 'var(--pri-high)', marginTop: 6 }}>Couldn’t load users: {membersErr}</div>}

          <div style={{ marginTop: 14 }}>
            <button className="btn primary" onClick={issue} disabled={!selected || issuing}>
              {issuing ? 'Issuing…' : 'Issue pass'}
            </button>
          </div>
        </div>

        {/* Result */}
        {result && result.status === 'issued' && (
          <div style={{ marginTop: 16, border: '1px solid #5EEAD4', background: '#F0FDFA', borderRadius: 8, padding: '16px 18px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0F766E', marginBottom: 10 }}>✓ Pass issued for {result.targetUpn}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <code style={{ fontSize: 20, fontWeight: 700, letterSpacing: 2, fontFamily: 'var(--mono)', color: '#0F766E', background: '#fff', border: '1px solid #5EEAD4', borderRadius: 6, padding: '8px 14px' }}>
                {result.pass || '(not returned)'}
              </code>
              {result.pass && (
                <button className="btn" onClick={copyPass}>{copied ? 'Copied' : 'Copy'}</button>
              )}
              <button className="btn" onClick={reset} title="Clear the pass from the screen">Done</button>
            </div>
            <div style={{ fontSize: 12.5, color: '#0F766E', marginTop: 10, lineHeight: 1.5 }}>
              Single use, expires {result.tapExpiresAt ? `at ${fmtWhen(result.tapExpiresAt)}` : 'in ~1 hour'}. Give it to the user now, it won't be shown again.
            </div>
          </div>
        )}
        {result && result.status === 'refused' && (
          <div style={{ marginTop: 16, border: '1px solid #FCD34D', background: '#FFFBEB', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#92400E', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div><strong>Refused.</strong> {result.detail || 'This user cannot be issued a pass (admin, ineligible, or not found).'}</div>
            <button className="btn" onClick={() => setResult(null)}>Dismiss</button>
          </div>
        )}
        {result && result.status === 'error' && (
          <div style={{ marginTop: 16, border: '1px solid #FCA5A5', background: '#FEF2F2', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#991B1B', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div><strong>Something went wrong.</strong> {result.detail || 'Try again.'}</div>
            <button className="btn" onClick={() => setResult(null)}>Dismiss</button>
          </div>
        )}

        {/* History */}
        <div style={{ marginTop: 28 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>Recent issuances</div>
          {history === null ? (
            <div style={{ fontSize: 13, color: 'var(--text-subtle)' }}>Loading…</div>
          ) : history.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-subtle)' }}>Nothing issued yet.</div>
          ) : (
            <div style={{ border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', overflowY: 'auto', maxHeight: 420, background: 'var(--bg-elev)' }}>
              {history.map((h, i) => {
                const s = STATUS_STYLE[h.status] || STATUS_STYLE.error;
                return (
                  <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: i < history.length - 1 ? '1px solid var(--line)' : 'none' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: s.color, background: s.bg, padding: '2px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>{s.label}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.targetUpn}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-subtle)' }}>by {h.issuerName || h.issuerEmail} · {fmtWhen(h.createdAt)}{h.detail ? ` · ${h.detail}` : ''}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
